import Image from "next/image";
import Link from "@/components/ui/Link";
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";

interface PhilanthropyFoundationData {
  anchor_title?: string;
  heading?: {
    title?: string;
  };
  text?: {
    text?: string;
  };
  quote?: {
    text?: string;
  };
  image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
  button?: {
    label?: string;
    href?: string;
  };
}

export const fields = [
  "anchor_title",
  "heading.title",
  "text.text",
  "quote.text",
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  "button.label",
  "button.href",
] as const;

export default function PhilanthropyFoundation({ data }: { data?: PhilanthropyFoundationData }) {
  const imageUrl = getReadableImageUrl(
    data?.image?.image_file?.image?.id || "",
    data?.image?.image_file?.image?.filename_download || ""
  );

  const sectionId = (data?.anchor_title || 'PhilanthropyFoundation')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .replace(/-{2,}/g, '-');

  return (
    <section id={sectionId} className="PhilanthropyFoundation">
      <div className="grid grid-cols-1 lg:grid-cols-[45%_1fr]">
        <div className="relative min-h-[300px] lg:min-h-0">
          {data?.image && imageUrl && (
            <Image
              src={imageUrl}
              alt={data.image.alt || ""}
              fill
              quality={95}
              loading="lazy"
              className="object-cover"
              sizes="(min-width: 1024px) 45vw, 100vw"
            />
          )}
        </div>
        <div className="flex flex-col justify-center px-6 py-12 lg:py-16 lg:pl-14 lg:pr-16">
          {data?.heading?.title && (
            <h2 className="font-freightBig text-[2.25rem] font-normal leading-[3rem] text-black mb-6">
              {data.heading.title}
            </h2>
          )}
          {data?.text?.text && (
            <div
              className="font-sans text-base font-normal leading-7 text-black [&>p]:mb-4 [&>p:last-child]:mb-0"
              dangerouslySetInnerHTML={sanitizeHTML(data.text.text)}
            />
          )}
          {data?.quote?.text && (
            <div className="bg-[#625959] rounded-[0.75rem] p-6 mt-6">
              <div
                className="font-sans text-base font-normal leading-7 text-white"
                dangerouslySetInnerHTML={sanitizeHTML(data.quote.text)}
              />
            </div>
          )}
          {data?.button?.href && (
            <div className="mt-8 text-left">
              <Link className="btn" href={data.button.href}>
                {data.button.label}
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
